"use client";

import { useEffect, useState } from "react";
import {
  ConsentStatus,
  applyConsent,
  getConsentCookie,
  setAnalyticsUser,
} from "@/lib/analytics";
import { useEmployeeInfoStore } from "@/modules/store/employeeStore";

export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

export function openCookieSettings(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentStatus | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const currentCookie = getConsentCookie();
    setConsent(currentCookie);
    if (!currentCookie) {
      setShowBanner(true);
    } else if (currentCookie === "granted") {
      // Re-apply in case gtag needs to be updated in current session
      applyConsent(true);
    }
    setIsInitialized(true);

    const handleOpenSettings = () => {
      setShowBanner(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  const accept = () => {
    applyConsent(true);
    setConsent("granted");
    setShowBanner(false);

    // If user is already logged in, associate analytics user_id now
    const currentEmployee = useEmployeeInfoStore.getState().employeeInfo;
    const authId =
      (currentEmployee as any)?.auth_id ||
      (currentEmployee as any)?.authId ||
      currentEmployee?.id;
    if (authId) {
      setAnalyticsUser(authId);
    }
  };

  const decline = () => {
    applyConsent(false);
    setConsent("denied");
    setShowBanner(false);
    setAnalyticsUser(null);
  };

  const openSettings = () => {
    setShowBanner(true);
  };

  const closeBanner = () => {
    setShowBanner(false);
  };

  return {
    consent,
    showBanner,
    isInitialized,
    accept,
    decline,
    openSettings,
    closeBanner,
  };
}
